<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        B2B WMS Status
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          color="secondary"
          outlined
          rounded
          small
          :loading="loadingOrders"
          :disabled="checking"
          @click="fetchOrders()">
          <v-icon
            small
            left>
            mdi-refresh
          </v-icon>
          โหลดรายการ
        </v-btn>
        <v-btn
          color="success"
          rounded
          small
          :loading="checking"
          :disabled="!canCheck"
          @click="checkSelected()">
          <v-icon
            small
            left>
            mdi-radar
          </v-icon>
          Tracking ({{ selectedCheckable.length }})
        </v-btn>
      </div>
    </div>

    <v-row class="px-8">
      <v-col
        cols="12"
        class="card-container elevation-3">
        <v-alert
          v-if="checking"
          type="info"
          dense
          text
          class="mb-4">
          {{ checkProgress }} — อาจใช้เวลาถึงประมาณ 2.5 นาที
          <v-progress-linear
            indeterminate
            color="info"
            class="mt-2" />
        </v-alert>

        <v-tabs
          v-model="tab"
          color="secondary">
          <v-tab>
            Orders ({{ selectedCheckable.length }}/{{ orders.length }})
          </v-tab>
          <v-tab>
            สินค้า ({{ itemRows.length }} รายการ)
          </v-tab>
        </v-tabs>

        <!-- Tab: เลือก order -->
        <div
          v-show="tab === 0"
          class="mt-4">
          <v-row dense>
            <v-col
              cols="12"
              md="3">
              <v-autocomplete
                v-model="filters.customer"
                :items="customerItems"
                :search-input.sync="customerSearch"
                :loading="loadingCustomers"
                item-text="mapName"
                item-value="number"
                label="Customer"
                prepend-inner-icon="mdi-account-tie"
                color="secondary"
                no-filter
                clearable
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="2">
              <v-select
                v-model="filters.state"
                :items="stateOptions"
                label="Sub Order Status"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="2">
              <v-select
                v-model="filters.channel"
                :items="channelOptions"
                label="Channel"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="2">
              <v-text-field
                v-model.trim="filters.search"
                label="ค้นหา SO / Main Order"
                prepend-inner-icon="mdi-magnify"
                color="secondary"
                clearable
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="3"
              class="d-flex align-center justify-end"
              style="gap: 0.5rem;">
              <v-btn
                small
                text
                color="secondary"
                :disabled="!filteredCheckable.length"
                @click="selectAllFiltered()">
                เลือกทั้งหมด ({{ filteredCheckable.length }})
              </v-btn>
              <v-btn
                small
                text
                :disabled="!selected.length"
                @click="selected = []">
                ล้างที่เลือก
              </v-btn>
            </v-col>
          </v-row>

          <div class="hint mt-3">
            แสดง SO ของ order B2B ที่สถานะ Reserved และ Completed · เลือก SO แล้วดูสินค้าทุก SO ได้ที่แท็บ "สินค้า"
          </div>

          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="filteredOrders"
            :loading="loadingOrders"
            :items-per-page="50"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
            item-key="orderId"
            sort-by="orderId"
            show-select
            class="orders-table mt-3">
            <template #[`item.mainOrderId`]="{ item }">
              <router-link
                v-if="item.mainOrderId"
                :to="orderLink(item.mainOrderId)"
                target="_blank">
                {{ item.mainOrderId }}
              </router-link>
              <span v-else>-</span>
            </template>
            <template #[`item.subOrderState`]="{ item }">
              <span class="state-label">
                <span
                  class="state-dot"
                  :style="{ background: stateColor(item.subOrderState) }" />
                {{ stateLabel(item.subOrderState) }}
              </span>
            </template>
            <template #[`item.qty`]="{ item }">
              {{ item.qty | showNumberFormat() }}
            </template>
            <template #[`item.customer`]="{ item }">
              <div>{{ item.customer }}</div>
              <div
                v-if="item.customerId"
                class="sub-text">
                {{ item.customerId }}
              </div>
            </template>
            <template #[`item.createdAt`]="{ item }">
              {{ item.createdAt | dateTimeUserFormat() }}
            </template>
            <template #[`item.wmsStatus`]="{ item }">
              <div
                v-if="wmsStatus(item)"
                class="py-2">
                <v-tooltip
                  bottom
                  :disabled="!wmsStatus(item).tooltip">
                  <template #activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      :color="wmsStatus(item).color"
                      class="wms-status-chip"
                      small
                      label
                      v-on="on">
                      {{ wmsStatus(item).text }}
                    </v-chip>
                  </template>
                  <span>{{ wmsStatus(item).tooltip }}</span>
                </v-tooltip>
                <v-btn
                  v-if="wmsStatus(item).retry"
                  x-small
                  text
                  color="secondary"
                  :disabled="checking"
                  @click="retryRow(item)">
                  <v-icon
                    x-small
                    left>
                    mdi-reload
                  </v-icon>
                  ลองใหม่
                </v-btn>
                <div
                  v-if="wmsStatus(item).sub"
                  class="sub-text">
                  {{ wmsStatus(item).sub }}
                </div>
                <div
                  v-if="pickSummary(item)"
                  class="pick-summary"
                  :class="{ 'pick-summary--short': isPickShort(item) }">
                  {{ pickSummary(item) }}
                </div>
              </div>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </div>

        <!-- Tab: สินค้าทุก SO ที่เลือก (1 แถว = 1 SKU ใน 1 SO) -->
        <div
          v-show="tab === 1"
          class="mt-4">
          <div
            class="d-flex flex-wrap align-center"
            style="gap: 0.5rem;">
            <v-chip
              small
              label>
              เลือก {{ selectedCheckable.length }} SO
            </v-chip>
            <v-chip
              small
              label
              outlined
              color="info">
              พบใน WMS {{ stateCounts.found }}
            </v-chip>
            <v-chip
              v-if="stateCounts.notFound"
              small
              label
              outlined
              color="grey">
              ไม่พบใน WMS {{ stateCounts.notFound }}
            </v-chip>
            <v-chip
              v-if="stateCounts.error"
              small
              label
              outlined
              color="warning">
              เช็คไม่ได้ {{ stateCounts.error }}
            </v-chip>
            <v-chip
              v-if="stateCounts.unchecked"
              small
              label
              outlined>
              ยังไม่เช็ค {{ stateCounts.unchecked }}
            </v-chip>
            <v-spacer />
            <span
              class="summary-total"
              :class="{ 'pick-summary--short': itemTotals.pickedQty < itemTotals.orderQty }">
              สั่ง {{ itemTotals.orderQty | showNumberFormat() }}
              · Pick แล้ว {{ itemTotals.pickedQty | showNumberFormat() }}
              · ยังไม่ pick {{ itemTotals.remainingQty | showNumberFormat() }} ชิ้น
            </span>
          </div>

          <v-alert
            v-if="stateCounts.unchecked || stateCounts.error"
            type="warning"
            dense
            text
            class="mt-3 mb-0">
            มี {{ stateCounts.unchecked + stateCounts.error }} SO ที่ยังไม่มีข้อมูลจาก WMS (ยังไม่เช็ค / เช็คไม่ได้)
            แถวของ SO เหล่านี้จะแสดง Pick แล้ว เป็น "-" — กด Search เพื่อเช็ค
          </v-alert>

          <v-row
            dense
            class="mt-3">
            <v-col
              cols="12"
              md="4">
              <v-text-field
                v-model.trim="itemFilters.search"
                label="ค้นหา SO / SKU / ชื่อสินค้า"
                prepend-inner-icon="mdi-magnify"
                color="secondary"
                clearable
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="4"
              class="d-flex align-center">
              <v-switch
                v-model="itemFilters.onlyShort"
                label="เฉพาะที่ยัง pick ไม่ครบ"
                color="secondary"
                class="mt-0"
                inset
                dense
                hide-details />
            </v-col>
            <v-col
              cols="12"
              md="4"
              class="d-flex align-center justify-end">
              <v-btn
                color="success"
                rounded
                small
                :disabled="!filteredItems.length"
                @click="exportItemsCsv()">
                <v-icon
                  small
                  left>
                  mdi-file-delimited-outline
                </v-icon>
                Export CSV ({{ filteredItems.length }})
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="itemHeaders"
            :items="filteredItems"
            :item-class="itemRowClass"
            :items-per-page="50"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
            no-data-text="ยังไม่ได้เลือก SO — เลือกในแท็บ Orders"
            item-key="key"
            class="items-table mt-3">
            <template #[`item.orderId`]="{ item }">
              <div class="font-weight-medium">
                {{ item.orderId }}
              </div>
              <div class="sub-text">
                <span class="state-label">
                  <span
                    class="state-dot"
                    :style="{ background: stateColor(item.subOrderState) }" />
                  {{ stateLabel(item.subOrderState) }}
                </span>
                · {{ item.warehouseName }}
              </div>
            </template>
            <template #[`item.code`]="{ item }">
              <span class="font-weight-medium">
                {{ item.code }}
              </span>
            </template>
            <template #[`item.color`]="{ item }">
              {{ item.color || '-' }}
            </template>
            <template #[`item.size`]="{ item }">
              {{ item.size || '-' }}
            </template>
            <template #[`item.name`]="{ item }">
              {{ item.name || '-' }}
            </template>
            <template #[`item.orderQty`]="{ item }">
              {{ item.orderQty | showNumberFormat() }}
            </template>
            <template #[`item.pickedQty`]="{ item }">
              <span
                v-if="item.hasWms"
                class="font-weight-bold"
                :class="item.isShort ? 'orange--text text--darken-3' : 'success--text'">
                {{ item.pickedQty | showNumberFormat() }}
              </span>
              <span v-else>-</span>
            </template>
            <template #[`item.remainingQty`]="{ item }">
              {{ item.remainingQty | showNumberFormat() }}
            </template>
            <template #[`item.wmsState`]="{ item }">
              <span :class="{ 'sub-text': !item.hasWms }">
                {{ item.wmsStateText }}
              </span>
            </template>
          </v-data-table>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { ExportToCsv } from 'export-to-csv'
import OrderProvider from '@/resources/OrderProvider'
import CustomersProvider from '@/resources/CustomersProvider'
import Ac2Provider from '@/resources/Ac2Provider'
import Ac2WarehouseProvider from '@/resources/Ac2WarehouseProvider'
import CheckOrderStatusState from '@/assets/js/CheckOrderStatusState'

const OrderService = new OrderProvider()
const CustomersService = new CustomersProvider()
const Ac2Service = new Ac2Provider()
const Ac2WarehouseService = new Ac2WarehouseProvider()

const B2B_CHANNELS = [
  { text: 'B2B (THB)', value: 'b2b_thb' },
  { text: 'B2B (USD)', value: 'b2b_usd' }
]

// สถานะ sub order ที่ดึงมาแสดง (backend รับ currentState ทีละค่า จึงยิงแยกต่อ channel × state)
const LOAD_STATES = ['reserved', 'completed']

const ORDER_PAGE_LIMIT = 200
const ORDER_MAX_PAGES = 50

// สี / label สถานะ sub order ชุดเดียวกับหน้า order list (NewOrderList.vue)
const ORDER_STATES = {
  pending: { label: 'Pending', color: '#cddc39' },
  reserved: { label: 'Reserved', color: '#ff9800' },
  paid: { label: 'Paid', color: '#9c27b0' },
  waiting: { label: 'Waiting', color: '#00bcd4' },
  ready_to_ship: { label: 'RTS', color: '#e91e8c' },
  completed: { label: 'Completed', color: '#1a2744' },
  exchange_return: { label: 'E/R', color: '#4caf50' },
  void: { label: 'Void', color: '#9e9e9e' },
  expired: { label: 'Expired', color: '#795548' },
  deleted: { label: 'Deleted', color: '#f44336' }
}

// backend รับได้สูงสุด 500 ใบต่อ request (1 request = 1 warehouse)
const WMS_STATUS_MAX = 500

const WMS_ERROR_TEXT = {
  NETWORK: 'ac2-queues / WMS ไม่ตอบหรือเกินเวลา',
  TOKEN: 'ขอ token WMS ไม่ได้',
  HTTP: 'ac2-queues / WMS ตอบ error',
  WMS_BUSINESS: 'WMS ตอบ fail หรือผลถูกตัดหน้า',
  INVALID_RESPONSE: 'response จาก WMS รูปแบบผิด'
}

// SKU เดียวกันอาจอยู่หลายบรรทัดใน SO → รวมตาม itemSku, qty ที่เป็น null = WMS ยังไม่มีค่า ให้นับเป็น 0
const aggregateLinesBySku = (lines) => {
  const map = new Map()
  const rows = lines || []

  rows.forEach((line) => {
    const sku = map.get(line.itemSku) || {
      itemSku: line.itemSku,
      itemDesc: null,
      lineNos: [],
      orderQty: 0,
      pickedQty: 0
    }

    sku.itemDesc = sku.itemDesc || line.itemDesc || null
    sku.lineNos.push(line.extLineNo)
    sku.orderQty += Number(line.orderQty) || 0
    sku.pickedQty += Number(line.pickedQty) || 0
    map.set(line.itemSku, sku)
  })

  return [...map.values()]
}

// sku ฝั่ง OMS ของ sub-order (code เดียวกับ item_sku ใน WMS) รวมตาม code
const aggregateOmsSkus = (skus) => {
  const map = new Map()

  skus.forEach((sku) => {
    const row = map.get(sku.code) || { ...sku, amount: 0 }
    row.amount += sku.amount
    map.set(sku.code, row)
  })

  return map
}

const chunk = (items, size) => {
  const chunks = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

export default {
  data () {
    return {
      tab: 0,
      // กำหนดความกว้างทุกคอลัมน์ ยกเว้น ลูกค้า ที่ยืดเต็มพื้นที่ที่เหลือ (และตัดบรรทัดได้)
      headers: [
        { text: 'SO', value: 'orderId', width: 160 },
        { text: 'Sub Order Status', value: 'subOrderState', width: 140 },
        { text: 'Main Order', value: 'mainOrderId', width: 150 },
        { text: 'Channel', value: 'channelText', width: 110 },
        { text: 'ลูกค้า', value: 'customer', cellClass: 'customer-cell' },
        { text: 'จำนวน', value: 'qty', align: 'end', width: 90 },
        { text: 'วันที่สร้าง', value: 'createdAt', width: 140 },
        { text: 'สถานะ WMS', value: 'wmsStatus', sortable: false, width: 190 }
      ].map((header) => ({ class: 'text-no-wrap', cellClass: 'text-no-wrap', ...header })),
      itemHeaders: [
        { text: 'SO', value: 'orderId' },
        { text: 'SKU', value: 'code' },
        { text: 'ชื่อสินค้า', value: 'name' },
        { text: 'สี', value: 'color' },
        { text: 'ไซส์', value: 'size' },
        { text: 'สั่ง', value: 'orderQty', align: 'end' },
        { text: 'Pick แล้ว', value: 'pickedQty', align: 'end' },
        { text: 'ยังไม่ pick', value: 'remainingQty', align: 'end' },
        { text: 'สถานะ WMS', value: 'wmsState' }
      ],
      channelOptions: [
        { text: 'ทั้งหมด', value: 'all' },
        ...B2B_CHANNELS
      ],
      stateOptions: [
        { text: 'ทั้งหมด', value: 'all' },
        ...LOAD_STATES.map((state) => ({ text: ORDER_STATES[state].label, value: state }))
      ],
      filters: {
        customer: null,
        state: 'all',
        channel: 'all',
        search: ''
      },
      itemFilters: {
        search: '',
        onlyShort: false
      },
      // ลูกค้าจาก /customers แบบเดียวกับหน้า order (CustomerList.vue) number = shippingAddress.customerId
      customerItems: [],
      customerSearch: null,
      customerSearchTimer: null,
      loadingCustomers: false,
      orders: [],
      selected: [],
      loadingOrders: false,
      // warehouseId -> true (ผูก AC2) / false (ไม่ได้ผูก) / ไม่มี key (ยังไม่รู้)
      ac2Warehouses: {},
      // SO -> ผลจาก /ac2/orders/wms-status
      wmsResults: {},
      checking: false,
      checkProgress: ''
    }
  },
  computed: {
    filteredOrders () {
      const search = (this.filters.search || '').toLowerCase()

      return this.orders
        .filter((order) => !this.filters.customer || order.customerId === this.filters.customer)
        .filter((order) => this.filters.state === 'all' || order.subOrderState === this.filters.state)
        .filter((order) => this.filters.channel === 'all' || order.channel === this.filters.channel)
        .filter((order) => !search
          || order.orderId.toLowerCase().includes(search)
          || (order.mainOrderId || '').toLowerCase().includes(search))
        .map((order) => ({ ...order, isSelectable: this.isCheckable(order) }))
    },
    filteredCheckable () {
      return this.filteredOrders.filter((order) => order.isSelectable)
    },
    selectedCheckable () {
      return this.selected.filter((order) => this.isCheckable(order))
    },
    canCheck () {
      return !this.checking && !this.loadingOrders && this.selectedCheckable.length > 0
    },
    stateCounts () {
      return this.selectedCheckable.reduce((acc, order) => {
        acc[this.resultState(order)]++
        return acc
      }, { found: 0, notFound: 0, error: 0, unchecked: 0 })
    },
    // 1 แถว = 1 SKU ใน 1 SO (SKU เดียวกันหลาย SO แสดงแยกแถว)
    // "สั่ง" ใช้ order_qty ของ WMS ถ้ามีบรรทัดนั้นใน WMS ไม่งั้นใช้จำนวนจาก OMS
    // allocated / pick / ส่ง จาก WMS เฉพาะ SO ที่ found, เรียงตาม SKU แล้วตาม SO ให้ SKU เดียวกันอยู่ติดกัน
    itemRows () {
      const rows = []

      this.selectedCheckable.forEach((order) => {
        const state = this.resultState(order)
        const hasWms = state === 'found'
        const wmsSkus = new Map((hasWms ? this.wmsResults[order.orderId].skus : []).map((sku) => [sku.itemSku, sku]))
        const omsSkus = aggregateOmsSkus(order.skus)
        const codes = new Set([...omsSkus.keys(), ...wmsSkus.keys()])

        codes.forEach((code) => {
          const oms = omsSkus.get(code)
          const wms = wmsSkus.get(code)
          const orderQty = wms ? wms.orderQty : (oms?.amount || 0)
          const pickedQty = wms?.pickedQty || 0

          rows.push({
            key: `${order.orderId}|${code}`,
            orderId: order.orderId,
            subOrderState: order.subOrderState,
            mainOrderId: order.mainOrderId,
            customer: order.customerId ? `${order.customer} (${order.customerId})` : order.customer,
            warehouseName: order.warehouseName,
            checkedAt: this.wmsResults[order.orderId]?.checkedAt || null,
            code,
            name: oms?.name || wms?.itemDesc || null,
            color: oms?.color || '',
            size: oms?.size || '',
            orderQty,
            pickedQty,
            remainingQty: Math.max(0, orderQty - pickedQty),
            hasWms,
            isShort: hasWms && pickedQty < orderQty,
            wmsStateText: this.rowStateText(order, state, wms)
          })
        })
      })

      return rows.sort((a, b) => a.code.localeCompare(b.code) || a.orderId.localeCompare(b.orderId))
    },
    filteredItems () {
      const search = (this.itemFilters.search || '').toLowerCase()

      return this.itemRows
        .filter((item) => !this.itemFilters.onlyShort || item.remainingQty > 0)
        .filter((item) => !search
          || item.orderId.toLowerCase().includes(search)
          || item.code.toLowerCase().includes(search)
          || (item.name || '').toLowerCase().includes(search))
    },
    itemTotals () {
      return this.itemRows.reduce((totals, item) => ({
        orderQty: totals.orderQty + item.orderQty,
        pickedQty: totals.pickedQty + item.pickedQty,
        remainingQty: totals.remainingQty + item.remainingQty
      }), { orderQty: 0, pickedQty: 0, remainingQty: 0 })
    }
  },
  watch: {
    customerSearch (val) {
      // ตอนเลือกแล้ว search-input จะกลายเป็นข้อความของตัวที่เลือก ไม่ต้องยิงค้นหาซ้ำ
      if (val && this.customerItems.some((item) => item.mapName === val)) {
        return
      }

      clearTimeout(this.customerSearchTimer)
      this.customerSearchTimer = setTimeout(() => this.fetchCustomers(), 500)
    }
  },
  mounted () {
    this.fetchCustomers()
    this.fetchOrders()
  },
  beforeDestroy () {
    clearTimeout(this.customerSearchTimer)
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async fetchCustomers () {
      try {
        this.loadingCustomers = true

        const { data } = await CustomersService.getItems({
          page: 1,
          limit: 50,
          search: this.customerSearch || ''
        })
        const items = (data?.results || []).map((item) => ({
          number: item.number,
          mapName: `${item.number} - ${item.searchName}`
        }))
        // เก็บตัวที่เลือกอยู่ไว้ใน list เสมอ ไม่งั้นช่องจะว่างเมื่อผลค้นหาใหม่ไม่มีตัวนั้น
        const selectedItem = this.customerItems.find((item) => item.number === this.filters.customer)

        this.customerItems = selectedItem && !items.some((item) => item.number === selectedItem.number)
          ? [selectedItem, ...items]
          : items
      } catch (error) {
        console.error('fetchCustomers', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loadingCustomers = false
      }
    },
    isCheckable (order) {
      return this.ac2Warehouses[order.warehouseId] !== false
    },
    resultState (order) {
      const result = this.wmsResults[order.orderId]

      if (!result) {
        return 'unchecked'
      }
      if (result.found === true) {
        return 'found'
      }

      return result.found === false ? 'notFound' : 'error'
    },
    orderLink (orderId) {
      return {
        name: 'NewOrderListByStatus',
        params: { orderStatus: 'all' },
        query: { id: orderId, is_main: 'true' }
      }
    },
    async fetchSubOrders (channel, currentState) {
      const results = []

      for (let page = 1; page <= ORDER_MAX_PAGES; page++) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await OrderService.getOrderListByWarehouse({
          channel,
          currentState,
          status: 'active',
          brand: 'all',
          sortBy: ['createdAt'],
          sortDesc: [true],
          page,
          limit: ORDER_PAGE_LIMIT
        })
        const pageResults = data?.results || []
        results.push(...pageResults)

        if (pageResults.length < ORDER_PAGE_LIMIT) {
          break
        }
      }

      return results
    },
    async fetchOrders () {
      try {
        this.loadingOrders = true

        const results = await Promise.all(B2B_CHANNELS.flatMap(({ value }) => LOAD_STATES.map((state) => this.fetchSubOrders(value, state))))
        const channelText = B2B_CHANNELS.reduce((obj, { text, value }) => ({ ...obj, [value]: text }), {})

        this.orders = results.flat().map((order) => {
          const skus = (order.skus || []).map((sku) => ({
            code: sku.code,
            name: sku.name,
            color: sku.color,
            size: sku.size,
            amount: Number(sku.amount) || 0
          }))
          // B2B เก็บลูกค้าไว้ใน shippingAddress (customerId = Customer No.)
          const address = order.shippingAddress || {}
          const customer = [address.title, address.firstName, address.lastName].filter(Boolean).join(' ') || '-'
          const customerId = address.customerId || null
          const subOrderState = CheckOrderStatusState(order)

          return {
            orderId: order.orderId,
            subOrderState,
            mainOrderId: order.reference?.orderId || null,
            channel: order.channel,
            channelText: channelText[order.channel] || order.channel,
            warehouseId: order.warehouse?.id,
            warehouseName: order.warehouse?.name || '-',
            customer,
            customerId,
            skus,
            qty: skus.reduce((sum, sku) => sum + sku.amount, 0),
            createdAt: order.createdAt,
            // ac2Sync (default null) ถูกเขียนหลังส่ง SO เข้า WMS แล้ว → ยังไม่มี = ยังรอ stock / ยังไม่ส่งเข้า WMS
            hasAc2Sync: !!order.ac2Sync,
            // แสดง "-" แทน Waiting Stock เมื่อ: ไม่มี field ac2Sync เลย (undefined)
            // หรือ sub order completed แล้วแต่ไม่มี ac2Sync (จบไปก่อนใช้ WMS)
            ac2SyncUnknown: order.ac2Sync === undefined || (subOrderState === 'completed' && !order.ac2Sync),
            ac2Synced: order.ac2Sync?.sync === true,
            ac2SyncedAt: order.ac2Sync?.updatedAt || null
          }
        })

        // แทน object ที่เลือกไว้ด้วยข้อมูลใหม่ (skus อาจเปลี่ยน) และตัดตัวที่หลุดจาก reserved ออก
        const orderMap = new Map(this.orders.map((order) => [order.orderId, order]))
        this.selected = this.selected
          .filter((order) => orderMap.has(order.orderId))
          .map((order) => orderMap.get(order.orderId))

        await this.resolveWarehouses()
      } catch (error) {
        console.error('fetchOrders', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loadingOrders = false
      }
    },
    async resolveWarehouses () {
      const warehouseIds = [...new Set(this.orders.map((order) => order.warehouseId).filter(Boolean))]
        .filter((id) => !(id in this.ac2Warehouses))

      await Promise.all(warehouseIds.map(async (warehouseId) => {
        try {
          const { data } = await Ac2WarehouseService.resolveAc2Warehouse(warehouseId)
          // data เป็น null = warehouse นี้ไม่ได้ผูก AC2 ไม่ต้องยิงเช็ค WMS
          this.$set(this.ac2Warehouses, warehouseId, !!data)
        } catch (error) {
          // resolve ไม่ได้ = ยังไม่รู้ ปล่อยให้เลือกได้ ถ้าไม่ได้ผูกจริง backend จะตอบ 400 กลับมาเอง
          console.error('resolveWarehouses', error)
        }
      }))

      this.selected = this.selected.filter((order) => this.isCheckable(order))
    },
    selectAllFiltered () {
      const selectedIds = new Set(this.selected.map((order) => order.orderId))

      this.selected = [
        ...this.selected,
        ...this.filteredCheckable.filter((order) => !selectedIds.has(order.orderId))
      ]
    },
    async checkSelected () {
      await this.runCheck(this.selectedCheckable)
      this.tab = 1
    },
    retryRow (order) {
      this.runCheck([order])
    },
    setResult (orderId, result) {
      // orderLines มีเฉพาะตอน found = true (false / null ไม่มีรายการสินค้า)
      const skus = aggregateLinesBySku(result.orderLines)
      const totals = result.found === true
        ? skus.reduce((sum, sku) => ({
          orderQty: sum.orderQty + sku.orderQty,
          pickedQty: sum.pickedQty + sku.pickedQty
        }), { orderQty: 0, pickedQty: 0 })
        : null

      this.$set(this.wmsResults, orderId, { ...result, skus, totals, checkedAt: Date.now() })
    },
    pickSummary (order) {
      const totals = this.wmsResults[order.orderId]?.totals

      const { showNumberFormat } = this.$options.filters

      return totals ? `Pick แล้ว ${showNumberFormat(totals.pickedQty)} / ${showNumberFormat(totals.orderQty)} ชิ้น` : ''
    },
    isPickShort (order) {
      const totals = this.wmsResults[order.orderId]?.totals

      return !!totals && totals.pickedQty < totals.orderQty
    },
    stateLabel (state) {
      return ORDER_STATES[state]?.label || state || '-'
    },
    stateColor (state) {
      return ORDER_STATES[state]?.color || '#bdbdbd'
    },
    itemRowClass (item) {
      return item.isShort ? 'sku-row--short' : ''
    },
    // export ตามแถวที่แสดงอยู่ (ผ่าน filter แล้ว) SO ที่ยังไม่มีข้อมูล WMS แสดง "-" เหมือนบนจอ
    exportItemsCsv () {
      try {
        const mapped = this.filteredItems.map((item) => ({
          'SO': item.orderId,
          'Sub Order Status': this.stateLabel(item.subOrderState),
          'Main Order': item.mainOrderId || '-',
          'Customer': item.customer,
          'Warehouse': item.warehouseName,
          'SKU': item.code,
          'ชื่อสินค้า': item.name || '-',
          'สี': item.color || '-',
          'ไซส์': item.size || '-',
          'สั่ง': item.orderQty,
          'Pick แล้ว': item.hasWms ? item.pickedQty : '-',
          'ยังไม่ pick': item.remainingQty,
          'สถานะ WMS': item.wmsStateText,
          'เช็คเมื่อ': item.checkedAt ? this.formatDate(item.checkedAt) : '-'
        }))

        const csvExporter = new ExportToCsv({
          filename: `b2b-wms-status-${this.$dayjs().format('YYYY-MM-DD-HHmm')}`,
          showLabels: true,
          useKeysAsHeaders: true
        })
        csvExporter.generateCsv(mapped)
      } catch (error) {
        console.error('exportItemsCsv', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      }
    },
    // ยังไม่มี ac2Sync และ WMS ยังไม่มีผล (ยังไม่เช็ค / ไม่พบ) → แสดง Waiting stock
    // ถ้าเช็คแล้ว WMS เจอ (หรือเช็คไม่ได้) ให้แสดงผลจาก WMS ตามปกติ
    isWaitingStock (order) {
      return !order.ac2SyncUnknown && !order.hasAc2Sync && ['unchecked', 'notFound'].includes(this.resultState(order))
    },
    // สถานะจาก ac2Sync ใช้ตอนยังไม่มีผลเช็คจาก WMS (หรือ WMS ไม่พบเพราะยังไม่ได้ส่ง)
    // ac2Sync undefined / completed ที่ไม่มี ac2Sync → null (แสดง "-")
    syncStatus (order) {
      if (order.ac2SyncUnknown) {
        return null
      }
      if (!order.hasAc2Sync) {
        return { text: 'Waiting Stock', color: 'error', tooltip: 'SO ยังไม่ถูกส่งเข้า WMS (ยังไม่มีข้อมูล ac2Sync)' }
      }
      if (!order.ac2Synced) {
        return { text: 'Sync ไม่สำเร็จ', color: 'error', tooltip: 'มีข้อมูล ac2Sync แต่ sync = false' }
      }

      return {
        text: 'SO Created',
        color: 'success',
        tooltip: 'ส่ง SO เข้า WMS แล้ว — กด Search เพื่อดูสถานะใน WMS',
        sub: order.ac2SyncedAt ? `${this.formatDate(order.ac2SyncedAt)}` : ''
      }
    },
    rowStateText (order, state, wms) {
      if (this.isWaitingStock(order) || state === 'unchecked') {
        return this.syncStatus(order)?.text || '-'
      }
      if (state === 'error') {
        return 'เช็คไม่ได้'
      }
      if (state === 'notFound') {
        return 'ไม่พบ SO ใน WMS'
      }
      if (!wms) {
        return 'SO ใน WMS ไม่มี SKU นี้'
      }

      return this.wmsStatus(order)?.text || '-'
    },
    async runCheck (orders) {
      if (this.checking || !orders.length) {
        return
      }

      // 1 request = 1 warehouse และไม่เกิน 500 ใบ ยิงทีละ request ห้ามยิงพร้อมกัน
      const groups = new Map()
      orders.forEach((order) => {
        const group = groups.get(order.warehouseId) || { name: order.warehouseName, orderIds: [] }
        group.orderIds.push(order.orderId)
        groups.set(order.warehouseId, group)
      })

      const requests = []
      groups.forEach((group, warehouseId) => {
        chunk(group.orderIds, WMS_STATUS_MAX).forEach((orderIds) => {
          requests.push({ warehouseId, name: group.name, orderIds })
        })
      })

      const errors = []

      try {
        this.checking = true

        for (const [index, request] of requests.entries()) {
          this.checkProgress = `กำลังเช็ค ${request.name} ${request.orderIds.length} ใบ (request ${index + 1}/${requests.length})`

          try {
            // eslint-disable-next-line no-await-in-loop
            const { data } = await Ac2Service.getWmsOrderStatuses({
              warehouse: Number(request.warehouseId),
              extOrderNo1List: request.orderIds
            })
            const items = data?.items || []

            items.forEach((item) => this.setResult(item.extOrderNo1, item))
          } catch (error) {
            const message = error?.message || 'Request failed'

            if (/not AC2-managed/i.test(message)) {
              this.$set(this.ac2Warehouses, request.warehouseId, false)
            }

            request.orderIds.forEach((orderId) => this.setResult(orderId, { found: null, error: 'REQUEST', message }))
            errors.push(`${request.name}: ${message}`)
          }
        }
      } finally {
        this.checking = false
        this.checkProgress = ''
        this.selected = this.selected.filter((order) => this.isCheckable(order))
      }

      this.notifySummary(orders, errors)
    },
    notifySummary (orders, errors) {
      const results = orders.map((order) => this.wmsResults[order.orderId]).filter(Boolean)
      const found = results.filter((result) => result.found === true).length
      const notFound = results.filter((result) => result.found === false).length
      const unknown = results.filter((result) => result.found === null).length

      this.setSnackbar({
        value: true,
        message: errors.length
          ? `เช็คไม่สำเร็จบางส่วน — ${errors.join(', ')}`
          : `เช็คแล้ว ${orders.length} ใบ: พบใน WMS ${found}, ไม่พบ ${notFound}, เช็คไม่ได้ ${unknown}`,
        type: errors.length || unknown ? 'warning' : 'success'
      })
    },
    formatDate (value) {
      return value ? this.$dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
    },
    wmsStatus (order) {
      const result = this.wmsResults[order.orderId]

      // warehouse ไม่ได้ผูก AC2 → เช็ค WMS ไม่ได้ (แถวนี้เลือกไม่ได้)
      if (!this.isCheckable(order)) {
        return {
          text: 'ไม่ได้ผูก AC2',
          color: 'grey',
          tooltip: `Warehouse ${order.warehouseName} ไม่ได้ผูกกับ AC2 จึงเช็คสถานะใน WMS ไม่ได้`
        }
      }

      if (this.isWaitingStock(order)) {
        return {
          ...this.syncStatus(order),
          sub: result ? `เช็คเมื่อ ${this.formatDate(result.checkedAt)}` : ''
        }
      }

      if (!result) {
        return this.syncStatus(order)
      }

      const checkedAt = `เช็คเมื่อ ${this.formatDate(result.checkedAt)}`

      if (result.found === null) {
        return {
          text: 'เช็คไม่ได้',
          color: 'warning',
          tooltip: result.message || WMS_ERROR_TEXT[result.error] || result.error || 'ไม่ทราบสาเหตุ',
          sub: `${result.error || '-'} · ${checkedAt}`,
          retry: true
        }
      }

      if (result.found === false) {
        return { text: 'ไม่พบใน WMS', color: 'grey', sub: checkedAt }
      }

      const statusTooltip = [result.ordStatusCode, result.ordStatusDescription].filter(Boolean).join(' · ')

      if (result.cancelReason) {
        return { text: 'ยกเลิกใน WMS', color: 'error', tooltip: result.cancelReason, sub: `${result.cancelReason} · ${checkedAt}` }
      }

      if (result.fullyShipped) {
        return { text: 'ส่งครบแล้ว', color: 'success', tooltip: statusTooltip, sub: checkedAt }
      }

      if (result.lastShippedDate) {
        return { text: 'ส่งบางส่วน', color: 'orange', tooltip: statusTooltip, sub: checkedAt }
      }

      return {
        text: result.ordStatusDescription || result.ordStatusCode || 'พบใน WMS',
        color: 'info',
        tooltip: statusTooltip,
        sub: checkedAt
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.hint {
  font-size: 0.8rem;
  color: #757575;
}
.sub-text {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 2px;
}
.pick-summary {
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 2px;
  color: #2e7d32;
}
.summary-total {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2e7d32;
}
.pick-summary--short {
  color: #e65100;
}
/* chip สถานะ WMS กว้างเท่ากันทุกแถว ข้อความยาวตัด ... (ดูเต็มใน tooltip) */
.wms-status-chip {
  width: 104px;
  justify-content: center;
}
.wms-status-chip ::v-deep .v-chip__content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.orders-table ::v-deep td.customer-cell {
  min-width: 180px;
}
.state-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.state-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.items-table ::v-deep tr.sku-row--short {
  background-color: #fff3e0;
}
</style>
